import BaseModel from './base'
import CompanyService from '../../services/CompanyService'
import moment from 'moment'
import store from '../../store'
import { faThumbsDown } from '@fortawesome/fontawesome-free-regular'

export enum CustomerType {
  DIRECT = 'direct',
  AGENCY = 'agency',
}

export enum PaUsageValues {
  SAAS = 'saas',
  CLIENT_BUSINESS = 'client_business',
  OWN_BUSINESS = 'own_business',
}

export interface IOnboardingInfo {
  pending: boolean
  haveWebsite?: boolean
  websiteUrl?: string
  industryServed?: string
  customerCount?: string
  tools?: string[]
  location?: boolean
  conversationDemo?: boolean
  locationId?: string
  snapshotId?: string
}

export interface ICompany {
  logoUrl: string
  name: string
  email: string
  phone: string
  website: string
  domain: string
  spareDomain: string
  privacyPolicy: string,
  termsConditions: string,
  customJavascript: string
  customCss: string
  theme: string
  address: string
  city: string
  postalCode: string
  country: string
  state: string
  timezone: string
  relationshipNumber: string
  termsOfServiceVersion: string
  termsOfServiceAcceptedBy: string
  termsOfServiceAcceptedDate: moment.Moment
  privacyPolicyVersion: string
  privacyPolicyAcceptedBy: string
  privacyPolicyAcceptedDate: moment.Moment
  affiliatePolicyVersion: string
  affiliatePolicyAcceptedBy: string
  affiliatePolicyAcceptedDate: moment.Moment
  isReselling: boolean
  onboardingInfo: IOnboardingInfo
  stripeId: string,
  upgradeEnabledForClients: boolean
  cancelEnabledForClients: boolean
}

export default class Company extends BaseModel {
  static requestBodyWhiteList = [
    'logoUrl',
    'name',
    'email',
    'phone',
    'website',
    'domain',
    'spareDomain',
    'privacyPolicy',
    'customJavascript',
    'customCss',
    'theme',
    'address',
    'city',
    'postalCode',
    'country',
    'state',
    'timezone',
    'termsOfServiceVersion',
    'termsOfServiceAcceptedBy',
    'termsOfServiceAcceptedDate',
    'privacyPolicyVersion',
    'privacyPolicyAcceptedBy',
    'privacyPolicyAcceptedDate',
    'affiliatePolicyVersion',
    'affiliatePolicyAcceptedBy',
    'affiliatePolicyAcceptedDate',
    'isReselling',
    'onboardingInfo',
    'stripeId'
  ]

  // Methods
  constructor(data: ICompany) {
    super(Company.requestBodyWhiteList, data)
  }

  async save(): Promise<{ [key: string]: any }> {
    return new Promise(async (resolve, reject) => {
      try {
        let response: { [key: string]: any }
        if (!this.customerType) {
          throw new Error('Problem while updating company')
        }
        if (!this.newRecord) {
          response = await CompanyService.update(this.id, this.updatedData)
          await store.dispatch('company/update', response.company)
        }
        resolve(response)
      } catch (error) {
        reject(error)
      }
    })
  }

  static async getById(id: string): Promise<Company> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await CompanyService.read(id)
        resolve(new Company(response.company))
      } catch (error) {
        reject(error)
      }
    })
  }

  get customerType(): CustomerType {
    return this.data.customerType
  }

  set customerType(value: CustomerType) {
    this.data.customerType = value
  }

  get country(): string {
    return this.data.country
  }

  set country(value: string) {
    this.data.country = value
    this.updatedData.country = value
  }

  get timezone(): string {
    return this.data.timezone
  }

  set timezone(value: string) {
    this.data.timezone = value
    this.updatedData.timezone = value
  }

  get name(): string {
    return this.data.name
  }

  set name(value: string) {
    this.data.name = value
    this.updatedData.name = value
  }

  get email(): string {
    return this.data.email
  }

  set email(value: string) {
    this.data.email = value
    this.updatedData.email = value
  }

  get logoUrl(): string {
    return this.data.logoUrl
  }

  set logoUrl(value: string) {
    this.data.logoUrl = value
    this.updatedData.logoUrl = value
  }

  get phone(): string {
    return this.data.phone
  }

  set phone(value: string) {
    this.data.phone = value
    this.updatedData.phone = value
  }

  get website(): string {
    return this.data.website
  }

  set website(value: string) {
    this.data.website = value
    this.updatedData.website = value
  }

  get domain(): string {
    return this.data.domain
  }

  set domain(value: string) {
    this.data.domain = value
    this.updatedData.domain = value
  }

  get spareDomain(): string {
    return this.data.spareDomain
  }

  set spareDomain(value: string) {
    this.data.spareDomain = value
    this.updatedData.spareDomain = value
  }

  get privacyPolicy(): string {
    return this.data.privacyPolicy
  }

  set privacyPolicy(value: string) {
    this.data.privacyPolicy = value
    this.updatedData.privacyPolicy = value
  }

  get termsConditions(): string {
    return this.data.termsConditions
  }

  set termsConditions(value: string) {
    this.data.termsConditions = value
    this.updatedData.termsConditions = value
  }

  get customJavascript(): string {
    return this.data.customJavascript
  }

  set customJavascript(value: string) {
    this.data.customJavascript = value
    this.updatedData.customJavascript = value
  }

  get customCss(): string {
    return this.data.customCss
  }

  set customCss(value: string) {
    this.data.customCss = value
    this.updatedData.customCss = value
  }

  get theme(): string {
    return this.data.theme
  }

  set theme(value: string) {
    this.data.theme = value
    this.updatedData.theme = value
  }

  get address(): string {
    return this.data.address
  }

  set address(value: string) {
    this.data.address = value
    this.updatedData.address = value
  }

  get city(): string {
    return this.data.city
  }

  set city(value: string) {
    this.data.city = value
    this.updatedData.city = value
  }
  get postalCode(): string {
    return this.data.postalCode
  }

  set postalCode(value: string) {
    this.data.postalCode = value
    this.updatedData.postalCode = value
  }
  get state(): string {
    return this.data.state
  }

  set state(value: string) {
    this.data.state = value
    this.updatedData.state = value
  }
  get relationshipNumber(): string {
    return this.data.relationshipNumber
  }

  set relationshipNumber(value: string) {
    this.data.relationshipNumber = value
  }

  get faviconUrl(): string {
    return this.data.faviconUrl
  }

  set faviconUrl(value: string) {
    this.data.faviconUrl = value
  }

  get currency(): string {
    return this.data.currency
  }

  set currency(value: string) {
    this.data.currency = value
  }

  get plan(): number {
    return this.data.plan
  }

  set plan(value: number) {
    this.data.plan = value
  }

  get subdomain(): string {
    return this.data.subdomain
  }

  set subdomain(value: string) {
    this.data.subdomain = value
  }

  get paUsage(): PaUsageValues[] {
    return this.data.paUsage || []
  }

  set paUsage(values: PaUsageValues[]) {
    this.data.paUsage = values
    this.updatedData.paUsage = values
  }

  get showSurveyModal() {
    return moment(this.dateAdded).isBefore(moment().subtract(60, 'days')) && this.paUsage.length === 0
  }

  get termsOfServiceVersion(): string {
    return this.data.termsOfServiceVersion
  }

  set termsOfServiceVersion(termsOfServiceVersion: string) {
    this.data.termsOfServiceVersion = termsOfServiceVersion
    this.updatedData.termsOfServiceVersion = termsOfServiceVersion
  }

  get termsOfServiceAcceptedBy(): string {
    return this.data.termsOfServiceAcceptedBy
  }

  set termsOfServiceAcceptedBy(userId: string) {
    this.data.termsOfServiceAcceptedBy = userId
    this.updatedData.termsOfServiceAcceptedBy = userId
  }

  get termsOfServiceAcceptedDate(): moment.Moment {
    return this.data.termsOfServiceAcceptedDate
  }

  set termsOfServiceAcceptedDate(dateApproved: moment.Moment) {
    this.data.termsOfServiceAcceptedDate = dateApproved.toDate()
    this.updatedData.termsOfServiceAcceptedDate = dateApproved.toDate()
  }

  get isReselling(): boolean {
    return this.data.isReselling
  }

  set isReselling(reselling: boolean) {
    this.data.isReselling = reselling
    this.updatedData.isReselling = reselling
  }

  get privacyPolicyVersion(): string {
    return this.data.privacyPolicyVersion
  }

  set privacyPolicyVersion(privacyPolicyVersion: string) {
    this.data.privacyPolicyVersion = privacyPolicyVersion
    this.updatedData.privacyPolicyVersion = privacyPolicyVersion
  }

  get privacyPolicyAcceptedBy(): string {
    return this.data.privacyPolicyAcceptedBy
  }

  set privacyPolicyAcceptedBy(userId: string) {
    this.data.privacyPolicyAcceptedBy = userId
    this.updatedData.privacyPolicyAcceptedBy = userId
  }

  get privacyPolicyAcceptedDate(): moment.Moment {
    return this.data.privacyPolicyAcceptedDate
  }

  set privacyPolicyAcceptedDate(dateApproved: moment.Moment) {
    this.data.privacyPolicyAcceptedDate = dateApproved.toDate()
    this.updatedData.privacyPolicyAcceptedDate = dateApproved.toDate()
  }

  get affiliatePolicyVersion(): string {
    return this.data.affiliatePolicyVersion
  }

  set affiliatePolicyVersion(affiliatePolicyVersion: string) {
    this.data.affiliatePolicyVersion = affiliatePolicyVersion
    this.updatedData.affiliatePolicyVersion = affiliatePolicyVersion
  }

  get affiliatePolicyAcceptedBy(): string {
    return this.data.affiliatePolicyAcceptedBy
  }

  set affiliatePolicyAcceptedBy(userId: string) {
    this.data.affiliatePolicyAcceptedBy = userId
    this.updatedData.affiliatePolicyAcceptedBy = userId
  }

  get affiliatePolicyAcceptedDate(): moment.Moment {
    return this.data.affiliatePolicyAcceptedDate
  }

  set affiliatePolicyAcceptedDate(dateApproved: moment.Moment) {
    this.data.affiliatePolicyAcceptedDate = dateApproved.toDate()
    this.updatedData.affiliatePolicyAcceptedDate = dateApproved.toDate()
  }

  get onboardingInfo(): IOnboardingInfo {
    return this.data.onboardingInfo
  }

  set onboardingInfo(values: IOnboardingInfo) {
    this.data.onboardingInfo = values
    this.updatedData.onboardingInfo = values
  }

  get stripeId(): string {
    return this.data.stripeId
  }

  set stripeId(stripeId: string) {
    this.data.stripeId = stripeId
  }

  get upgradeEnabledForClients():boolean{
    return this.data.upgradeEnabledForClients;
  }

  set upgradeEnabledForClients(upgradeFlag:boolean){
    this.data.upgradeEnabledForClients = upgradeFlag;
  }

  get cancelEnabledForClients():boolean{
    return this.data.cancelEnabledForClients;
  }

  set cancelEnabledForClients(cancelFlag:boolean){
    this.data.cancelEnabledForClients = cancelFlag;
  }
}
